import {
    Input,
    Button,
    Typography
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, DocumentPlusIcon, EyeIcon, EyeSlashIcon, UserCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { deleteOtherDocument, addCustomer, resetStatus, getBusinessType } from "../../store/slices/customerSlice";
import ShowLoader from "@/components/Loader";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';
import { fetchCountryMobileCode } from "../../store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import { Helmet } from 'react-helmet';
import ScrollToTop from "@/components/ScrollToTop";
import {
    CountryOption,
    CountrySingleValue
} from "@/components/CountrySelectComponents";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

// const fileValidation = (field) =>
//     yup
//         .mixed()
//         .test("fileType", "Only image files are allowed", (value) => {
//             if (!value || !(value instanceof File)) return true;
//             return value.type.startsWith("image/");
//         })
//         .nullable()
//         .required(
//             field === "residence_image"
//                 ? "Residence ID or Iqama ID image is required"
//                 : `${field
//                     .replaceAll("_", " ")
//                     .replace(/^./, (str) => str.toUpperCase())} is required`
//         );

const fileValidation = (field) =>
    yup
        .mixed()
        .test("fileType", "Only image or PDF files are allowed", (value) => {
            if (!value || !(value instanceof File)) return true;

            const allowedImage = value.type.startsWith("image/");
            const allowedPdf = value.type === "application/pdf";

            return allowedImage || allowedPdf;
        })
        .nullable()
        .required(
            field === "residence_image"
                ? "Residence ID or Iqama ID image/PDF is required"
                : `${field
                    .replaceAll("_", " ")
                    .replace(/^./, (str) => str.toUpperCase())} is required`
        );

const schema = () =>
    yup.object({
        first_name: yup
            .string()
            .trim()
            .required("First name is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "First name cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "First name cannot start or end with punctuation")
            .min(2, "First name must be at least 2 characters")
            .max(30, "First name cannot exceed 30 characters"),

        last_name: yup
            .string()
            .trim()
            .required("Last name is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Last name cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Last name cannot start or end with punctuation")
            .min(2, "Last name must be at least 2 characters")
            .max(30, "Last name cannot exceed 30 characters"),

        company_name: yup
            .string()
            .trim()
            .required("Company name is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Company name cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Company name cannot start or end with punctuation")
            .min(2, "Company name must be at least 2 characters")
            .max(50, "Company name cannot exceed 50 characters"),

        business_type: yup
            .string()
            .required("Business type is required")
            .min(1, "Business type must be at least 2 characters")
            .max(50, "Business type cannot exceed 50 characters"),

        gender: yup
            .string()
            .nullable()
            .oneOf(['Male', 'Female', 'Other'], "Please select a valid gender"),

        email: yup
            .string()
            .trim()
            .required("Email address is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .email("Invalid email address")
            .max(50, "Email address cannot exceed 50 characters"),

        mobile_number: yup
            .string()
            .trim()
            .required("Phone number is required")
            .matches(notBlankRegex, "Phone number cannot be empty or just spaces")
            .matches(/^\d+$/, "Phone number must contain only digits")
            .min(8, "Phone number must be at least 8 digits")
            .max(15, "Phone number cannot exceed 15 digits")
            .test("not-all-zeros", "Phone number cannot be all zeros", (value) => {
                if (!value) return false;
                return !/^0+$/.test(value);
            }),

        country_code: yup
            .string()
            .trim()
            .required("Country code is required")
            .max(5, "Country code cannot exceed 5 characters"),

        alternate_number: yup
            .string()
            .nullable()
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .trim()
            .test("not-blank", "This field cannot be empty or just spaces", (value) => !value || notBlankRegex.test(value))
            .test("is-valid", "Phone number must be 8–15 digits", (value) => !value || /^[0-9]{8,15}$/.test(value))
            .test("not-all-zeros", "Phone number cannot be all zeros", (value) => !value || !/^0+$/.test(value)),

        alternate_country_code: yup
            .string()
            .nullable()
            .trim()
            .max(5, "Alternate country code cannot exceed 5 characters")
            .transform((value, originalValue) => (originalValue === "" ? null : value))
            .when("alternate_number", {
                is: (val) => val && val.trim() !== "",
                then: (schema) => schema.required("Alternate country code is required when alternate number is provided"),
                otherwise: (schema) => schema.nullable(),
            }),

        password: yup
            .string()
            .required("Password is required")
            .matches(/\S/, "Password cannot contain only spaces")
            .min(8, "Password must be at least 8 characters")
            .max(15, "Password cannot exceed 15 characters")
            .test(
                "is-strong",
                "Password is weak — use uppercase, lowercase, number & special character",
                (value) =>
                    value &&
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(value)
            ),

        password_confirmation: yup
            .string()
            .when("password", {
                is: (val) => val && val.length > 0,
                then: (schema) =>
                    schema
                        .required("Confirm password is required")
                        .oneOf([yup.ref("password"), null], "Password and Confirm Password must match") // run this second
                        .min(8, "Password must be at least 8 characters")
                        .max(15, "Password cannot exceed 15 characters"),
                otherwise: (schema) => schema.notRequired(),
            }),

        permanent_address: yup
            .string()
            .when([], {
                is: () => true,
                then: (schema) =>
                    schema
                        .required("National Address is required")
                        .min(2, "National Address must be at least 2 characters")
                        .max(100, "National Address cannot exceed 100 characters"),
                otherwise: (schema) => schema.notRequired(),
            })
            .trim()
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Address cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Address cannot start or end with punctuation"),

        address_proof_image: fileValidation("address_proof_image"),
        commercial_reg_image: fileValidation("commercial_reg_image"),
        vat_certificate_image: fileValidation("vat_certificate_image"),
        passbook_image: fileValidation("passbook_image"),
        residence_image: fileValidation("residence_image"),

        commercial_reg_number: yup
            .string()
            .trim()
            .required("Commercial Registration Certificate Number is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Commercial Registration number cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Commercial Registration number cannot start or end with punctuation")
            .min(2, "Commercial Registration number must be at least 2 characters")
            .max(30, "Commercial Registration number cannot exceed 30 characters"),

        vat_certificate_number: yup
            .string()
            .trim()
            .required("VAT Certificate number is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "VAT Certificate number cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "VAT Certificate number cannot start or end with punctuation")
            .min(2, "VAT Certificate number must be at least 2 characters")
            .max(30, "VAT Certificate number cannot exceed 30 characters"),

        account_number: yup
            .string()
            .trim()
            .required("Account number is required")
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Account number cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Account number cannot start or end with punctuation")
            .min(8, "Account number must be at least 8 characters")
            .max(15, "Account number cannot exceed 15 characters"),

        bank_swift_code: yup
                    .string()
                    .required("Bank IBAN is required")
                    .length(24, "Bank IBAN must be exactly 24 characters")
                    .matches(/^[A-Z0-9]{24}$/, "Invalid IBAN format"),


        account_holder_first_name: yup
            .string()
            .nullable()
            .transform((value) => (value === "" ? null : value))
            .trim()
            .matches(notBlankRegex, "This field cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "First name cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "First name cannot start or end with punctuation")
            .min(2, "First name must be at least 2 characters")
            .max(30, "First name cannot exceed 30 characters"),

        // account_holder_last_name: yup
        //     .string()
        //     .nullable()
        //     .transform((value) => (value === "" ? null : value))
        //     .trim()
        //     .matches(notBlankRegex, "This field cannot be empty or just spaces")
        //     .matches(noHTMLRegex, "HTML tags are not allowed")
        //     .matches(noCodeRegex, "Code content is not allowed")
        //     .matches(noConsecutiveSpecialChars, "Last name cannot contain consecutive special characters")
        //     .matches(noLeadingTrailingPunct, "Last name cannot start or end with punctuation")
        //     .min(2, "Last name must be at least 2 characters")
        //     .max(30, "Last name cannot exceed 30 characters"),

        other_documents: yup
            .mixed()
            .nullable()
            .test("fileType", "Only PDF or image files are allowed", (value) => {
                if (!value) return true;
                const files = Array.isArray(value) ? value : [value];
                return files.every((file) => {
                    if (typeof file === "string") return true;

                    if (file?.file && typeof file.file === "string") return true;

                    if (file instanceof File) {
                        return file.type.startsWith("image/") || file.type === "application/pdf";
                    }

                    return false;
                });
            })
            .test("fileCount", "You can upload up to 5 files only", (value) => {
                if (!value) return true;
                const files = Array.isArray(value) ? value : [value];
                return files.length <= 5;
            }),

        residence_id: yup
            .string()
            .trim()
            .required("Residence ID is required")
            .matches(notBlankRegex, "Residence ID cannot be empty or just spaces")
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Residence ID cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Residence ID cannot start or end with punctuation")
            .min(2, "Residence ID must be between 2 and 30 characters/digits")
            .max(30, "Residence ID must be between 2 and 30 characters/digits"),
        
        other_documents_detail: yup
            .string()
            .nullable()
            .test(
                "not-only-spaces",
                "This field cannot contain only spaces",
                (value) => {
                    if (value === null || value === undefined) return true; // allow null
                    if (value === "") return true; // allow empty
                    return value.trim().length > 0; // reject only-space values
                }
            )
            .matches(noHTMLRegex, "HTML tags are not allowed")
            .matches(noCodeRegex, "Code content is not allowed")
            .matches(noConsecutiveSpecialChars, "Other documents detail cannot contain consecutive special characters")
            .matches(noLeadingTrailingPunct, "Other documents detail cannot start or end with punctuation")
            .max(100, "Other documents detail cannot exceed 100 characters")

    });



export function RegisterVendor() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const initialForm = {
        id:null,
        first_name: '',
        last_name: '',
        company_name: '',
        business_type: '',
        gender: '',
        email: '',
        mobile_number: null,
        country_code: null,
        alternate_number: null,
        alternate_country_code: null,
        password: '',
        password_confirmation: '',
        permanent_address: null,
        address_proof_image: '',
        commercial_reg_number: '',
        commercial_reg_image: '',
        vat_certificate_number: '',
        vat_certificate_image: '',
        account_number: '',
        passbook_image: null,
        bank_swift_code: 'SA',
        account_holder_first_name: null,
        // account_holder_last_name: null,
        other_documents: [],
        role: 'Vendor',
        residence_id: '',
        residence_image: null,
        other_documents_detail: null
    };

    const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema()),
        defaultValues: initialForm
    });

    useEffect(() => {
        dispatch(getBusinessType());
        dispatch(fetchCountryMobileCode());
    }, []);

    const selectedCustomer = useSelector((s) => s.customer.selected);
    const { loading, business_types } = useSelector((state) => state.customer);
    const { mobile_country_code } = useSelector((state) => state.internalUser);

    const other_documents = watch('other_documents') || [];
    const passbook_image = watch('passbook_image');
    const residence_image = watch('residence_image');
    const address_proof_image = watch('address_proof_image');
    const vat_certificate_image = watch('vat_certificate_image');
    const commercial_reg_image = watch('commercial_reg_image');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const togglePassword = () => {
        setShowPassword(!showPassword);
    }
    const toggleConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    }

    const onSubmit = async (data) => {

        try {
            const result = await dispatch(addCustomer(data)).unwrap();
            if (result.status) {
                Toaster.success("Vendor registered successfully! You can now log in to the admin portal using your credentials.");
                dispatch(resetStatus());
                reset();
            } else {
                Toaster.error(result.message || "Something went wrong!");
                dispatch(resetStatus());
            }
        } catch (err) {
            Toaster.error(err?.message || "Failed to add user");
            dispatch(resetStatus());
        }
    };

    const handleFileChange = (e, field) => {
        const files = Array.from(e.target.files);

        if (field === "other_documents") {
            const existingDocs = watch("other_documents") || [];

            setValue("other_documents", [...existingDocs, ...files], { shouldValidate: true });
        } else {
            setValue(field, files[0], { shouldValidate: true });
        }
    };

    const removeFile = (field) => {
        setValue(field, null, { shouldValidate: false });
    };

    const removeDocument = (index, documentId = null) => {
        const updated = [...other_documents];
        const removedDoc = updated.splice(index, 1);
        setValue("other_documents", updated);

        if (documentId) {
            Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!"
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(deleteOtherDocument(documentId)).unwrap().then(() => {
                        Toaster.success("Other document deleted successfully");
                    }).catch((error) => {
                        Toaster.error("Failed to delete document");
                        setValue("other_documents", [...updated, removedDoc[0]]);
                    });
                } else {
                    setValue("other_documents", [...updated, removedDoc[0]]);
                }
            });
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Helmet>
                <title>Avon Vendor Portal</title>
            </Helmet>
            {loading && <ShowLoader />}
             <ScrollToTop />
            <header className="w-full bg-[#F7F5F2] px-6 py-3 shadow-md flex justify-between items-center z-20">
                <div className="flex items-center gap-3">
                    <img
                        src="/img/logos/logo.png"
                        alt="Corporate Supplier Logo"
                        className="w-[64px] h-[50px] object-contain"
                    />
                    <div className="flex flex-col leading-[100%]">
                        <span className="font-lato font-bold text-[22px] text-[#1A1918] mb-3">Corporate</span>
                        <span className="font-lato font-bold text-[22px] text-[#1A1918] ">Supplier</span>
                    </div>
                </div>
                <a
                    href="#"
                    className="flex items-center gap-2 text-sm text-gray-700 font-medium hover:text-black"
                >
                    <UserCircleIcon className="h-5 w-5" />
                    <span>Admin Support</span>
                </a>
            </header>

            <main
                className="relative flex-grow w-full flex items-center justify-center p-4"
                style={{ backgroundImage: "url('/img/bgImg.png')", backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
                <div className="absolute inset-0 bg-black opacity-40 z-0"></div>

                <div className="relative z-10 w-full bg-white rounded-lg shadow-xl p-8 mb-4">
                    <Typography variant="h4" color="blue-gray" className="mb-6 text-left font-bold">
                        Vendor Registration Portal
                    </Typography>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
                        <fieldset>
                            <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                                <div className="mb-5 pb-2 border-b border-blue-gray-200">
                                    <Typography variant="h4" color="blue-gray">
                                        Basic Details
                                    </Typography>
                                </div>
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            First Name <span className="text-red-500">*</span>
                                        </Typography>
                                        <Input
                                            type="text"
                                            size="lg"
                                            placeholder="Enter First Name"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("first_name")}
                                            error={Boolean(errors.first_name)}
                                        />
                                        {errors.first_name && (
                                            <p className="text-red-500 text-sm">{errors.first_name?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Last Name <span className="text-red-500">*</span>
                                        </Typography>
                                        <Input
                                            type="text"
                                            size="lg"
                                            placeholder="Enter Last Name"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("last_name")}
                                            error={Boolean(errors.last_name)}
                                        />
                                        {errors.last_name && (
                                            <p className="text-red-500 text-sm">{errors.last_name?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Registered Company Name <span className="text-red-500">*</span>
                                        </Typography>
                                        <Input
                                            type="text"
                                            size="lg"
                                            placeholder="Enter Company Name"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("company_name")}
                                            error={Boolean(errors.company_name)}
                                        />
                                        {errors.company_name && (
                                            <p className="text-red-500 text-sm">{errors.company_name?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Business Type <span className="text-red-500">*</span>
                                        </Typography>
                                        <Select
                                            placeholder="Select Business Type"
                                            onChange={(business_type) => {
                                                setValue('business_type', business_type?.value, { shouldValidate: true });
                                            }}
                                            error={Boolean(errors.business_type)}
                                            options={business_types}
                                            value={business_types?.find(opt => opt.value == watch('business_type')) || null}
                                            isClearable
                                        />
                                        {errors.business_type && (
                                            <p className="text-red-500 text-sm">{errors.business_type?.message}</p>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Gender
                                        </Typography>
                                        <Select
                                            placeholder="Select Gender"
                                            options={[
                                                { value: 'Male', label: 'Male' },
                                                { value: 'Female', label: 'Female' },
                                                { value: 'Other', label: 'Other' }
                                            ]}
                                            onChange={(option) => {
                                                setValue('gender', option?.value, { shouldValidate: true });
                                            }}
                                            value={[
                                                { value: 'Male', label: 'Male' },
                                                { value: 'Female', label: 'Female' },
                                                { value: 'Other', label: 'Other' }
                                            ].find(opt => opt.value === watch('gender')) || null}
                                            isClearable
                                        />
                                        {errors.gender && (
                                            <p className="text-red-500 text-sm">{errors.gender?.message}</p>
                                        )}
                                    </div>

                                    <div className="xl:col-span-1 flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Phone Number <span className="text-red-500">*</span>(with WhatsApp Access)
                                        </Typography>
                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                            <div className="md:col-span-3">
                                                <Select
                                                    type="text"
                                                    size="lg"
                                                    className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                                                    labelProps={{
                                                        className: "before:content-none after:content-none",
                                                    }}
                                                    placeholder='Select...'
                                                    onChange={(code) => {
                                                        setValue('country_code', code?.value, { shouldValidate: true });
                                                    }}
                                                    value={mobile_country_code?.find(opt => opt.value === watch('country_code')) || null}
                                                    options={mobile_country_code}
                                                    error={Boolean(errors.country_code)}
                                                    isClearable
                                                    styles={{
                                                        control: (provided, state) => ({
                                                            ...provided,
                                                            minHeight: '44px',
                                                            height: '44px',
                                                        }),
                                                    }}
                                                    components={{
                                                        Option: CountryOption,
                                                        SingleValue: CountrySingleValue
                                                    }}
                                                />
                                            </div>
                                            <div className="md:col-span-3">
                                                <Input
                                                    type="number"
                                                    size="lg"
                                                    min='0'
                                                    placeholder="Enter Phone Number"
                                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                    labelProps={{
                                                        className: "before:content-none after:content-none",
                                                    }}
                                                    {...register("mobile_number")}
                                                    error={Boolean(errors.mobile_number)}
                                                />
                                            </div>
                                        </div>
                                        {(errors.mobile_number || errors.country_code) && (
                                            <p className="text-red-500 text-sm">{errors.mobile_number?.message ? errors.mobile_number?.message : errors.country_code.message}</p>
                                        )}
                                    </div>
                                    <div className="xl:col-span-1 flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Alternate Number
                                        </Typography>
                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                                            <div className="md:col-span-3">
                                                <Select
                                                    type="text"
                                                    placeholder='Select...'
                                                    onChange={(code) => {
                                                        setValue('alternate_country_code', code?.value, { shouldValidate: true });
                                                    }}
                                                    value={mobile_country_code?.find(opt => opt.value === watch('alternate_country_code')) || null}
                                                    options={mobile_country_code}
                                                    error={Boolean(errors.alternate_country_code)}
                                                    isClearable
                                                    styles={{
                                                        control: (provided, state) => ({
                                                            ...provided,
                                                            minHeight: '44px',
                                                            height: '44px',
                                                        }),
                                                    }}
                                                />
                                            </div>
                                            <div className="md:col-span-3">
                                                <Input
                                                    type="number"
                                                    size="lg"
                                                    min='0'
                                                    placeholder="Enter Alternate Number"
                                                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                    labelProps={{
                                                        className: "before:content-none after:content-none",
                                                    }}
                                                    {...register("alternate_number")}
                                                    error={Boolean(errors.alternate_number)}
                                                    onChange={(e) => {
                                                        let value = e.target.value;
                                                        if (!value || value.trim() === "") {
                                                            setValue("alternate_number", '', { shouldValidate: true });
                                                            setValue("alternate_country_code", null, { shouldValidate: true });
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {(errors.alternate_number || errors.alternate_country_code) && (
                                            <p className="text-red-500 text-sm">{errors.alternate_number?.message ? errors.alternate_number?.message : errors.alternate_country_code.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Password <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="password-wrapper">
                                            <Input
                                                type={showPassword ? "text" : "password"}
                                                size="lg"
                                                placeholder="********"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("password")}
                                                error={Boolean(errors.password)}
                                            />
                                            {showPassword ? (
                                                <EyeIcon className="eye-icon" onClick={togglePassword} />
                                            ) : (
                                                <EyeSlashIcon className="eye-icon" onClick={togglePassword}></EyeSlashIcon>
                                            )}
                                        </div>
                                        {errors.password && (
                                            <p className="text-red-500 text-sm">{errors.password?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Confirm Password: <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="password-wrapper">
                                            <Input
                                                type={showConfirmPassword ? "text" : "password"}
                                                size="lg"
                                                placeholder="********"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("password_confirmation")}
                                                error={Boolean(errors.password_confirmation)}
                                            />
                                            {showConfirmPassword ? (
                                                <EyeIcon className="eye-icon" onClick={toggleConfirmPassword} />
                                            ) : (
                                                <EyeSlashIcon className="eye-icon" onClick={toggleConfirmPassword}></EyeSlashIcon>
                                            )}
                                        </div>
                                        {errors.password_confirmation && (
                                            <p className="text-red-500 text-sm">{errors.password_confirmation?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Email address <span className="text-red-500">*</span>
                                        </Typography>
                                        <Input
                                            type="email"
                                            size="lg"
                                            placeholder="Enter Email Address"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("email")}
                                            error={Boolean(errors.email)}
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-sm">{errors.email?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Residence ID or Iqama ID <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="text"
                                                size="lg"
                                                placeholder="Enter Residence ID or Iqama ID"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("residence_id")}
                                                error={Boolean(errors.residence_id)}
                                            />
                                            <input
                                                id="residenceNumberFile"
                                                type="file"
                                               accept="image/*,application/pdf"
                                                multiple
                                                className="hidden"
                                                {...register("residence_image")}
                                                onChange={(e) => handleFileChange(e, 'residence_image')}
                                            />
                                            <label
                                                htmlFor="residenceNumberFile"
                                                className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> Choose File
                                            </label>
                                        </div>
                                        {(errors.residence_id || errors.residence_image) && (
                                            <p className="text-red-500 text-sm">{errors.residence_id?.message ? errors.residence_id?.message : errors.residence_image?.message}</p>
                                        )}
                                    </div>
                                    {residence_image && <>
                                        <div className="flex flex-col gap-1">
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                                <FilePreview
                                                    file={residence_image}
                                                    onRemove={() => removeFile("residence_image")}
                                                    isView={false}
                                                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                />
                                            </div>
                                        </div>
                                    </>}
                                </div>
                            </div>
                            <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white">
                                <div className="mb-5 pb-2 border-b border-blue-gray-200">
                                    <Typography variant="h4" color="blue-gray">
                                        KYC DETAILS
                                    </Typography>
                                </div>
                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                    <div className="md:col-span-2">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            National Address <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="text"
                                                size="lg"
                                                placeholder="Apartment 12B, Building 5, King Abdullah Road, Al Olaya District, Riyadh 12211, Kingdom of Saudi Arabia"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("permanent_address")}
                                                error={Boolean(errors.permanent_address)}
                                            />
                                            <input
                                                id="addressFile"
                                                type="file"
                                               accept="image/*,application/pdf"
                                                multiple
                                                className="hidden"
                                                {...register("address_proof_image")}
                                                onChange={(e) => handleFileChange(e, 'address_proof_image')}
                                            />
                                            <label
                                                htmlFor="addressFile"
                                                className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> Choose File
                                            </label>
                                        </div>
                                        {(errors.permanent_address || errors.address_proof_image) && (
                                            <p className="text-red-500 text-sm">{errors.permanent_address?.message ? errors.permanent_address?.message : errors.address_proof_image?.message}</p>
                                        )}
                                    </div>
                                    {address_proof_image &&
                                        <div className="md:col-span-2">
                                            <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                                <FilePreview
                                                    file={address_proof_image}
                                                    onRemove={() => removeFile("address_proof_image")}
                                                    isView={false}
                                                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                />
                                            </div>
                                        </div>}
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Commercial Registration Certificate Number <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="text"
                                                size="lg"
                                                placeholder="Enter Certificate Number"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("commercial_reg_number")}
                                                error={Boolean(errors.commercial_reg_number)}
                                            />
                                            <input
                                                id="commercialNumberFile"
                                                type="file"
                                               accept="image/*,application/pdf"
                                                multiple
                                                className="hidden"
                                                onChange={(e) => handleFileChange(e, 'commercial_reg_image')}
                                            />
                                            <label
                                                htmlFor="commercialNumberFile"
                                                className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> Choose File
                                            </label>
                                        </div>
                                        {(errors.commercial_reg_number || errors.commercial_reg_image) && (
                                            <p className="text-red-500 text-sm">{errors.commercial_reg_number?.message ? errors.commercial_reg_number?.message : errors.commercial_reg_image?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            VAT Certificate Number <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="text"
                                                size="lg"
                                                placeholder="Enter VAT Number"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("vat_certificate_number")}
                                                error={Boolean(errors.vat_certificate_number)}
                                            />
                                            <input
                                                id="vatNumberFile"
                                                type="file"
                                               accept="image/*,application/pdf"
                                                multiple
                                                className="hidden"
                                                onChange={(e) => handleFileChange(e, 'vat_certificate_image')}
                                            />
                                            <label
                                                htmlFor="vatNumberFile"
                                                className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> Choose File
                                            </label>
                                        </div>
                                        {(errors.vat_certificate_number || errors.vat_certificate_image) && (
                                            <p className="text-red-500 text-sm">{errors.vat_certificate_number?.message ? errors.vat_certificate_number?.message : errors.vat_certificate_image?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {commercial_reg_image && (
                                            <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                                <FilePreview
                                                    file={commercial_reg_image}
                                                    onRemove={() => removeFile("commercial_reg_image")}
                                                    isView={false}
                                                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {vat_certificate_image && (
                                            <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                                <FilePreview
                                                    file={vat_certificate_image}
                                                    onRemove={() => removeFile("vat_certificate_image")}
                                                    isView={false}
                                                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Account Number <span className="text-red-500">*</span>
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="number"
                                                size="lg"
                                                placeholder="Enter Account Number"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register("account_number")}
                                                error={Boolean(errors.account_number)}
                                            />
                                            <input
                                                id="accountNumberFile"
                                                type="file"
                                               accept="image/*,application/pdf"
                                                multiple
                                                className="hidden"
                                                onChange={(e) => handleFileChange(e, 'passbook_image')}
                                            />
                                            <label
                                                htmlFor="accountNumberFile"
                                                className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> Choose File
                                            </label>
                                        </div>
                                        {(errors.account_number || errors.passbook_image) && (
                                            <p className="text-red-500 text-sm">{errors.account_number?.message ? errors.account_number?.message : errors.passbook_image?.message}</p>
                                        )}

                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Bank IBAN <span className="text-red-500">*</span>
                                        </Typography>
                                        <Input
                                            type="text"
                                            size="lg"
                                            maxLength="24"
                                            placeholder="Enter Bank IBAN"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("bank_swift_code", {
                                                onChange: (e) => {
                                                    e.target.value = e.target.value.toUpperCase();
                                                },
                                            })}
                                            error={Boolean(errors.bank_swift_code)}
                                        />
                                        {errors.bank_swift_code && (
                                            <p className="text-red-500 text-sm">{errors.bank_swift_code?.message}</p>
                                        )}
                                    </div>
                                    {passbook_image && <>
                                        <div className="md:col-span-2">
                                            <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">
                                                <FilePreview
                                                    file={passbook_image}
                                                    onRemove={() => removeFile("passbook_image")}
                                                    isView={false}
                                                    containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                />
                                            </div>
                                        </div>
                                    </>}
                                    <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Beneficiary Account Holder Full Name
                                        </Typography>
                                        <Input
                                            type="text"
                                            size="lg"
                                            placeholder="Enter Full Name"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("account_holder_first_name")}
                                            error={Boolean(errors.account_holder_first_name)}
                                        />
                                        {errors.account_holder_first_name && (
                                            <p className="text-red-500 text-sm">{errors.account_holder_first_name?.message}</p>
                                        )}
                                    </div>
                                    {/* <div className="flex flex-col gap-1">
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Account Holder Last Name
                                        </Typography>
                                        <Input
                                            type="text"
                                            size="lg"
                                            placeholder="Enter Last Name"
                                            className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            {...register("account_holder_last_name")}
                                            error={Boolean(errors.account_holder_last_name)}
                                        />
                                        {errors.account_holder_last_name && (
                                            <p className="text-red-500 text-sm">{errors.account_holder_last_name?.message}</p>
                                        )}
                                    </div> */}
                                    <div>
                                        <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                                            Other Documents
                                        </Typography>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="text"
                                                size="lg"
                                                placeholder="Choose Other Document"
                                                className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                {...register('other_documents_detail')}
                                                // isDisabled={true}
                                                // value={
                                                //     other_documents && other_documents.length > 0
                                                //         ? other_documents
                                                //             .map((doc) =>
                                                //                 doc?.id ? doc.document_name : doc.name || doc?.file?.name
                                                //             )
                                                //             .join(", ")
                                                //         : ""
                                                // }
                                            />
                                            <input
                                                id="otherDocumentFile"
                                                type="file"
                                               accept="image/*,application/pdf"
                                                multiple
                                                className="hidden"
                                                onChange={(e) => handleFileChange(e, "other_documents")}
                                            />
                                            <label
                                                htmlFor="otherDocumentFile"
                                                className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                                            >
                                                <DocumentPlusIcon className="h-4 w-4 text-green-800" />
                                                Choose File
                                            </label>
                                        </div>
                                        {errors.other_documents_detail && (
                                            <p className="text-red-500 text-sm">{errors.other_documents_detail?.message}</p>
                                        )}
                                        {errors?.other_documents && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {errors.other_documents.message || "Invalid file type"}
                                            </p>
                                        )}
                                    </div>
                                    {other_documents.length > 0 && (
                                        <div className="md:col-span-2">
                                            <div className="mt-3 flex flex-wrap gap-6">
                                                {other_documents?.map((doc, index) => {
                                                    const isExisting = !!doc?.id;
                                                    const file = isExisting ? doc.file : doc;
                                                    const docName = isExisting ? doc.document_name : doc.name || doc?.file?.name || "Document";

                                                    return (
                                                        <FilePreview
                                                            key={index}
                                                            file={file}
                                                            onRemove={() => removeDocument(index, isExisting ? doc.id : "")}
                                                            isView={false}
                                                            containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                                            className="object-cover w-full h-full"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </fieldset>
                        <div className="flex items-center gap-2 justify-end">
                            <Button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                                {loading ? "Saving..." : "Submit"}
                            </Button>
                        </div>
                    </form>
                </div>
                <Typography variant="small" className="absolute bottom-1 mt-0 left-0 right-0 text-center text-white/80 z-10">
                    ©{new Date().getFullYear()} Avon International company. All rights reserved.
                </Typography>
            </main>
        </div>
    );
}

export default RegisterVendor;